/* hs-eslint ignored failing-rules */

/* eslint-disable no-bitwise */
'use es6';

import { findIndex, each, range, pick, isEqual, isNumber } from 'underscore';
import text from 'I18n/utils/unescapedText';
import SeamlessImmutable from 'seamless-immutable';
import { getFieldPath as getFieldAncestorPath } from 'ContentComponents/cmv2/utils';
import { getDefaultScalarValue, valueIsArray } from 'ContentUtils/helpers/FieldHelpers';
import { GROUP, HTML } from 'ContentUtils/constants/CustomWidgetFieldTypes';
import CustomWidgetField from 'DesignData/models/CustomWidgetField';
import { AS_CHILD, AFTER } from 'DesignData/enums/DragDropCustomWidgetFieldInsertTypes';
import { MAX_NESTING_DEPTH } from 'DesignData/constants/CustomWidgets';
import { createSearchRegExp, hasSearchMatch } from 'DesignData/lib/text';
import { isNullOrUndefined } from 'DesignData/lib/validation';
/**
 * Creates the IDECodeMirror sessionId for a Codepane.
 * @param  {Number}                       customWidgetId
 * @param  {CustomWidgetEditorPaneTypes}  paneId
 * @return {String}
 */

export function createCodePaneSessionId(customWidgetId, paneId) {
  return `${customWidgetId}:${paneId}`;
}
const occurrenceKey = 'DesignEditorsUI.customWidgetEditor.sidebar.fieldEdit.contentOptions.occurrences.fieldLabel';
export function getOccurrenceLabel(fieldLabel, fieldKey) {
  return text(occurrenceKey, {
    occurrenceNumber: fieldKey[fieldKey.length - 1],
    occurrenceLabel: fieldLabel
  });
}
export function getNormalizedFields(fieldsList, isInitialFetch = false) {
  const fieldsMap = {};
  const fieldsOrder = [];
  fieldsList.forEach(_field => {
    const field = isInitialFetch ? new CustomWidgetField(_field) : _field;
    fieldsOrder.push(field.id);
    fieldsMap[field.id] = field;
  });
  return SeamlessImmutable({
    fields: fieldsMap,
    fieldsOrder
  });
}
export function getDenormalizedFields(fieldsState) {
  return fieldsState.fieldsOrder.map(_fieldId => {
    return fieldsState.fields[_fieldId];
  });
}
export function findMatchingFields(fields, searchTerm, searchChildren = true) {
  const searchRegExp = createSearchRegExp(searchTerm);
  return fields.reduce((fieldsList, _field) => {
    const {
      label,
      name,
      type
    } = _field;
    const matchesSearchTerm = hasSearchMatch(searchRegExp, label, name, type);

    if (matchesSearchTerm) {
      fieldsList.push(_field);
    } else if (_field.children && searchChildren) {
      const matchingChildren = findMatchingFields(_field.children, searchTerm);
      fieldsList.push(...matchingChildren);
    }

    return fieldsList;
  }, []);
}
export function getControllingFields(fieldsList, fieldName, parentName) {
  return fieldsList.reduce((list, _field) => {
    const _fieldName = parentName ? `${parentName}.${_field.name}` : _field.name;

    if (_field.name !== fieldName) {
      list.push(Object.assign({}, _field, {
        name: _fieldName
      }));

      if (_field.children) {
        list = list.concat(getControllingFields(_field.children, fieldName, _fieldName));
      }
    }

    return list;
  }, []);
}

function getFieldNameMap(fields) {
  let result = {};
  each(fields, _field => {
    result[_field.name] = _field;

    if (_field.children) {
      result = Object.assign({}, result, {}, getFieldNameMap(_field.children));
    }
  });
  return result;
}

export function getUniqueFieldName(fieldType, fieldsMap, newFieldNameRoot) {
  const fieldNameDict = getFieldNameMap(fieldsMap);

  if (!newFieldNameRoot) {
    newFieldNameRoot = fieldType === GROUP ? 'field_group' : `${fieldType}_field`;
  }

  let newFieldName = newFieldNameRoot;

  for (let index = 0; index < Object.values(fieldNameDict || {}).length; index++) {
    if (!fieldNameDict[newFieldName]) {
      return newFieldName;
    }

    newFieldName = `${newFieldNameRoot}_${index + 1}`;
  }

  return newFieldName;
} // returns object that maps field ids to their labels

export function getFieldLabels(fieldsList) {
  let result = {};
  fieldsList.forEach(_field => {
    result[_field.id] = _field.label || _field.name || _field.id;

    if (_field.children) {
      result = Object.assign({}, result, {}, getFieldLabels(_field.children));
    }
  });
  return result;
} // helper to getFieldPath when fieldId is in a child somewhere

function getPathFromChildren(children, fieldId) {
  const index = findIndex(children, _child => _child.id === fieldId);

  if (~index) {
    return ['children', index];
  }

  for (let i = 0; i < children.length; i += 1) {
    const _child = children[i];

    if (_child.children) {
      const _path = getPathFromChildren(_child.children, fieldId);

      if (_path.length) {
        return ['children', i, ..._path];
      }
    }
  }

  return [];
}
/*
 Get immutable path to field
 ex: getFieldPath({id: 'id1', children: [{id: 'id2'}]}, 'id2') -> ['id1', 'children', 0]
*/


export function getFieldPath(fieldsMap, fieldId) {
  if (fieldsMap[fieldId]) {
    return [fieldId];
  }

  for (const _fieldId of Object.keys(fieldsMap)) {
    const _field = fieldsMap[_fieldId];

    if (_field.children) {
      const childPath = getPathFromChildren(_field.children, fieldId);

      if (childPath.length) {
        return [_fieldId, ...childPath];
      }
    }
  }

  return [];
}

function getFieldFromChildren(children, fieldId) {
  let field = children.find(_child => _child.id === fieldId);

  if (field) {
    return field;
  }

  for (const _child of children) {
    if (_child.children) {
      field = getFieldFromChildren(_child.children, fieldId);

      if (field) {
        return field;
      }
    }
  }

  return null;
}

export function getField(fieldsMap, fieldId) {
  let field = fieldsMap[fieldId];

  if (field) {
    return field;
  }

  for (const _fieldId of Object.keys(fieldsMap)) {
    const _field = fieldsMap[_fieldId];

    if (_field.children) {
      field = getFieldFromChildren(_field.children, fieldId);

      if (field) {
        return field;
      }
    }
  }

  return null;
}

function getFieldWithTranslationPathFromChildren(children, fieldId, path = '', includeBase = true) {
  // console.log('Get child fields', children, path);
  const field = children.find(__child => __child.id === fieldId);

  if (field) {
    return {
      field,
      path: includeBase ? `${path}.${field.name}` : path
    };
  }

  for (const __child of children) {
    if (__child.children) {
      const nextPath = path ? `${path}.${__child.name}` : __child.name;
      const fieldAndPath = getFieldWithTranslationPathFromChildren(__child.children, fieldId, nextPath, includeBase);

      if (fieldAndPath) {
        return fieldAndPath;
      }
    }
  }

  return null;
}

export function getFieldWithTranslationPath(fieldsMap, fieldId, includeBase = true) {
  // console.log('Get fields', fieldsMap);
  const field = fieldsMap[fieldId];

  if (field) {
    return {
      field,
      path: includeBase ? field.name : ''
    };
  }

  for (const __fieldId of Object.keys(fieldsMap)) {
    const __field = fieldsMap[__fieldId];

    if (__field.children) {
      const fieldAndPath = getFieldWithTranslationPathFromChildren(__field.children, fieldId, __field.name, includeBase);

      if (fieldAndPath) {
        return fieldAndPath;
      }
    }
  }

  return null;
}

function getFieldDirectParentFromField(field, fieldId) {
  if (!field.children) {
    return null;
  }

  if (~findIndex(field.children, _child => _child.id === fieldId)) {
    return field;
  }

  for (const _child of field.children) {
    const directParent = getFieldDirectParentFromField(_child, fieldId);

    if (directParent) {
      return directParent;
    }
  }

  return null;
}

function getFieldDirectParent(fieldsMap, fieldId) {
  if (fieldsMap[fieldId]) {
    return null;
  }

  for (const _fieldId of Object.keys(fieldsMap)) {
    const _field = fieldsMap[_fieldId];

    const directParent = _field && getFieldDirectParentFromField(_field, fieldId);

    if (directParent) {
      return directParent;
    }
  }

  return null;
}

export function isChild(field, childId) {
  if (field.children) {
    return !!getFieldFromChildren(field.children, childId);
  }

  return false;
}
/*
 returns array of breadcrumb items containing id/label leading
 to but not including fieldId
 ex. getFieldBreadCrumbs([{id: 1, name: 'parent'}], 1)
     -> []
     getFieldBreadCrumbs([{id: 1, name: 'parent', children: [{id: 2, name: 'child'}]}], 2)
     -> [
      {id: 1, name: 'parent', children: [{id: 2, name: 'child'}]},
    ]
*/

export function getFieldBreadCrumbs(fieldsList, fieldId) {
  for (const _field of fieldsList) {
    const fieldItem = pick(_field, 'id', 'label', 'name', 'tab');

    if (!fieldItem.label) {
      fieldItem.label = fieldItem.name;
    }

    if (_field.id === fieldId) {
      return [fieldItem];
    }

    if (isChild(_field, fieldId)) {
      return [fieldItem].concat(getFieldBreadCrumbs(_field.children, fieldId));
    }
  }

  return [];
}
/*
  Field is not copyable if it's a descendant of a repeating group
  Top level fields are always copyable
  ex. isFieldCopyable([{id: 1, occurence: { default: 4 }}], 1)
       -> true
      isFieldCopyable([{id: 1, occurrence:{ default: 4}, children: [{ id: 2 }]}], 2)
       -> false
*/

export function isFieldCopyable(fieldsList, fieldId) {
  const ancestors = getFieldAncestorPath(fieldsList, fieldId);
  const ancestorsWithoutSelf = ancestors.slice(0, ancestors.length - 1);

  for (const _ancestor of ancestorsWithoutSelf) {
    if (_ancestor.occurrence) return false;
  }

  return true;
}

function getFieldSize(field, currentSize = 1) {
  if (field.children && field.children.length) {
    const sizes = field.children.map(child => getFieldSize(child, currentSize + 1));
    return Math.max(...sizes);
  }

  return currentSize;
}

export function getFieldDepth(fieldsList, fieldId) {
  for (const _field of fieldsList) {
    if (_field.id === fieldId) {
      return 0;
    }

    const depthInChildren = _field.children && getFieldDepth(_field.children, fieldId);

    if (depthInChildren || depthInChildren === 0) {
      return 1 + depthInChildren;
    }
  }

  return null;
}

function getFieldChildDepth(field) {
  if (field.children) {
    return 1 + field.children.reduce((_max, _child) => {
      return Math.max(_max, getFieldChildDepth(_child));
    }, 0);
  }

  return 0;
}

export function isTooDeepOnDrag(fieldsMap, fieldId, replaceFieldId, insertType, maxNestingDepth = MAX_NESTING_DEPTH) {
  const replaceFieldPath = getFieldPath(fieldsMap, replaceFieldId);
  const replaceFieldDepth = replaceFieldPath.slice(1).length / 2;
  const fieldSize = getFieldSize(getField(fieldsMap, fieldId));
  let newDepth = replaceFieldDepth + fieldSize;

  if (insertType === AS_CHILD) {
    newDepth += 1;
  }

  return newDepth > maxNestingDepth;
}
export function addFieldToSelectedFields(selectedFields, field) {
  if (selectedFields[field.id]) {
    return selectedFields.without(field.id);
  } // if it's the child of an already selected field, don't select


  const isChildOfSelectedField = Object.keys(selectedFields).some(_fieldId => {
    return isChild(selectedFields[_fieldId], field.id);
  });

  if (isChildOfSelectedField) {
    return selectedFields;
  } // deselect any children of this field that are currently selected


  const childFieldIds = Object.keys(selectedFields).filter(_fieldId => {
    return isChild(field, _fieldId);
  });
  return selectedFields.without(childFieldIds).set(field.id, field);
}

function cannotBeGrouped(fieldsMap, fieldId, maxNestingDepth = MAX_NESTING_DEPTH) {
  const depth = getFieldDepth(Object.values(fieldsMap || {}), fieldId);
  const field = getField(fieldsMap, fieldId);
  const childDepth = getFieldChildDepth(field);
  return depth + childDepth >= maxNestingDepth - 1;
}

export function isTooDeepOnGroup(fieldsMap, selectedFields, maxNestingDepth = MAX_NESTING_DEPTH) {
  return Object.keys(selectedFields).some(_fieldId => cannotBeGrouped(fieldsMap, _fieldId, maxNestingDepth));
}
export function fieldsAreNotSiblings(fieldsMap, selectedFieldIds) {
  if (!selectedFieldIds.length) {
    return false;
  }

  const rootParent = getFieldDirectParent(fieldsMap, selectedFieldIds[0]);
  return selectedFieldIds.some(_fieldId => {
    const _parent = getFieldDirectParent(fieldsMap, _fieldId);

    const bothTopLevel = !rootParent && !_parent;
    const haveSameParent = rootParent && _parent && rootParent.id === _parent.id;
    return !(bothTopLevel || haveSameParent);
  });
}
const containsHtmlFieldCache = new WeakMap();
export function containsHtmlField(fields) {
  const cached = containsHtmlFieldCache.get(fields);

  if (cached !== undefined) {
    return cached;
  }

  const result = fields.some(_field => {
    return _field.type === HTML || _field.children && containsHtmlField(_field.children);
  });
  containsHtmlFieldCache.set(fields, result);
  return result;
}

function setFieldDefault(field, defaultValueOptions = {}) {
  let defaultValue = getDefaultScalarValue(field);

  if (field.type === GROUP && field.children) {
    defaultValue = {};
    const newChildren = field.children.map(_child => {
      const _newChild = setFieldDefault(_child, defaultValueOptions);

      if (!isNullOrUndefined(_newChild.default)) {
        defaultValue[_child.name] = _newChild.default;
      }

      return _newChild;
    });
    field = field.set('children', newChildren);
  }

  if (field.occurrence) {
    const numberOfOccurrences = field.occurrence.default || field.occurrence.min || field.type === GROUP && 0;

    if (!isNullOrUndefined(defaultValue) && isNumber(numberOfOccurrences)) {
      defaultValue = range(numberOfOccurrences).map(_index => {
        const {
          hasChangedDefault,
          hasChangedOccurrence,
          hasAddedOrDeletedChild,
          fieldId
        } = defaultValueOptions;

        if (!fieldId) {
          return defaultValue;
        }

        const previousDefault = field.default;
        let childWasChanged = false;

        if (field.type === GROUP && fieldId) {
          childWasChanged = hasAddedOrDeletedChild && field.id === fieldId || (hasChangedDefault || hasChangedOccurrence) && isChild(field, fieldId);
        }

        const valueWasOccurrenceArray = Array.isArray(previousDefault) && previousDefault.length && (!valueIsArray(field) || Array.isArray(previousDefault[0])) && previousDefault[_index] !== undefined; // If field is a group and its child's default was changed, change out the child's
        // value for the new one but keep other sibling's values the same as before
        // i.e. if previous default was [{ a: 1, b: 2}, { a: 3, b: 4}] and b's default
        // was changed to 10, return [{ a: 1, b: 10}, { a: 3, b: 10}]

        if (valueWasOccurrenceArray && childWasChanged) {
          const _previousDefault = previousDefault[_index];
          return field.children.reduce((newDefault, _child) => {
            const _fieldName = _child.name;

            const _selfChildWasChanged = _child.id === fieldId || isChild(_child, fieldId);

            if (_selfChildWasChanged || _previousDefault[_fieldName] === undefined && defaultValue[_fieldName] !== undefined) {
              newDefault[_fieldName] = defaultValue[_fieldName];
            } else if (_previousDefault[_fieldName] !== undefined) {
              newDefault[_fieldName] = _previousDefault[_fieldName];
            }

            return newDefault;
          }, {});
        }

        const selfWasChanged = hasChangedDefault && field.id === fieldId; // If unrelated field was changed, use same element as before
        // i.e. if previous default was [1,2,3] and another field was changed,
        // keep it as [1,2,3]

        if (valueWasOccurrenceArray && !selfWasChanged && !childWasChanged) {
          return previousDefault[_index];
        }

        return defaultValue;
      });
    }
  }

  if (!isNullOrUndefined(defaultValue)) {
    return field.set('default', defaultValue);
  }

  return field;
}

export function setFieldDefaults(fieldsMap, defaultValueOptions) {
  return Object.keys(fieldsMap).reduce((_fieldsMap, _fieldId) => {
    const _field = fieldsMap[_fieldId];
    return _fieldsMap.set(_fieldId, setFieldDefault(_field, defaultValueOptions));
  }, fieldsMap);
}
export function editField(fieldsMap, field, defaultValueOptions = {}) {
  const oldField = getField(fieldsMap, field.id);
  const hasChangedDefault = oldField && !isEqual(getDefaultScalarValue(oldField), getDefaultScalarValue(field));
  const hasChangedOccurrence = oldField && !isEqual(oldField.occurrence, field.occurrence);
  const hasAddedOrDeletedChild = oldField && field.type === GROUP && oldField.children.length !== field.children.length;

  const _defaultValueOptions = Object.assign({
    fieldId: field.id,
    hasChangedDefault,
    hasChangedOccurrence,
    hasAddedOrDeletedChild
  }, defaultValueOptions);

  const path = getFieldPath(fieldsMap, field.id);
  return setFieldDefaults(fieldsMap.setIn(path, field), _defaultValueOptions);
}
export function deleteField(fieldsState, idToDelete, defaultValueOptions) {
  if (fieldsState.fields[idToDelete]) {
    return fieldsState.merge({
      fields: fieldsState.fields.without(idToDelete),
      fieldsOrder: fieldsState.fieldsOrder.filter(_id => _id !== idToDelete)
    });
  }

  const directParent = getFieldDirectParent(fieldsState.fields, idToDelete);

  if (directParent) {
    const newParent = directParent.update('children', children => {
      return children.filter(_child => _child.id !== idToDelete);
    });
    return fieldsState.merge({
      fields: editField(fieldsState.fields, newParent, defaultValueOptions)
    });
  }

  return fieldsState;
}

function addChild(fieldsState, parentId, child, index) {
  const parent = getField(fieldsState.fields, parentId);

  if (!parent) {
    return fieldsState;
  }

  const newParent = parent.update('children', (children = []) => {
    index = index || index === 0 ? index : children.length;
    return children.slice(0, index).concat([child]).concat(children.slice(index, children.length));
  });
  const fields = editField(fieldsState.fields, newParent);
  return fieldsState.merge({
    fields
  });
}

export function createField(fieldsState, field, parentFieldId, index) {
  const parentField = parentFieldId && getField(fieldsState.fields, parentFieldId);

  if (parentField) {
    return addChild(fieldsState, parentFieldId, field, index);
  }

  const {
    fieldsOrder
  } = fieldsState;
  index = index || index === 0 ? index : fieldsOrder.length;
  const newFieldsOrder = fieldsOrder.slice(0, index).concat([field.id]).concat(fieldsOrder.slice(index, fieldsOrder.length));
  return fieldsState.merge({
    fields: fieldsState.fields.set(field.id, field),
    fieldsOrder: newFieldsOrder
  });
}

function removeIdsFromField(field) {
  // oldId does not get persisted
  // needed for resetVisibilityControllingField
  const fieldWithoutId = field.set('oldId', field.id).without('id');

  if (field.children) {
    return fieldWithoutId.update('children', (children = []) => {
      return children.map(removeIdsFromField);
    });
  }

  return fieldWithoutId;
}

const cloneFieldI18nKey = key => `designdata.models.customWidgetField.clone.${key}`;

function findFieldByOldId(field, fieldId) {
  if (field.oldId === fieldId) return field;

  if (field.type === GROUP) {
    for (const _child of field.children) {
      const maybeField = findFieldByOldId(_child, fieldId);
      if (maybeField) return maybeField;
    }
  }

  return null;
} // If controlling field in Display Conditions was within the group
// that is being cloned then update it with new ID


function resetVisibilityControllingFields(fieldGroup, topLevelGroup) {
  if (!topLevelGroup) {
    topLevelGroup = fieldGroup;
  }

  return fieldGroup.update('children', (children = []) => {
    return children.map(_child => {
      const controllingField = _child.visibility && findFieldByOldId(topLevelGroup, _child.visibility.controlling_field);

      if (controllingField) {
        _child = _child.setIn(['visibility', 'controlling_field'], controllingField.id);
      }

      if (_child.type === GROUP) {
        _child = resetVisibilityControllingFields(_child, fieldGroup);
      }

      return _child;
    });
  });
}

export function cloneField(fieldsState, fieldId) {
  const field = getField(fieldsState.fields, fieldId);
  let clonedField = removeIdsFromField(field).set('label', text(cloneFieldI18nKey('label'), {
    fieldLabel: field.label
  })).set('name', getUniqueFieldName(field.type, fieldsState.fields, `${field.name}_copy`));
  clonedField = new CustomWidgetField(clonedField);

  if (clonedField.type === GROUP) {
    clonedField = resetVisibilityControllingFields(clonedField);
  }

  const parentField = getFieldDirectParent(fieldsState.fields, fieldId);
  let index = parentField ? parentField.children.findIndex(_child => _child.id === fieldId) : fieldsState.fieldsOrder.findIndex(_fieldId => _fieldId === fieldId);

  if (!~index) {
    index = parentField ? parentField.children.length : fieldsState.fieldsOrder.length;
  } else {
    index += 1;
  }

  return {
    clonedField,
    parentFieldId: parentField && parentField.id,
    index
  };
}
export function moveField(fieldsState, fieldId, replaceFieldId, insertType) {
  const field = getField(fieldsState.fields, fieldId);
  const hasReorderedSiblings = !fieldsAreNotSiblings(fieldsState.fields, [fieldId, replaceFieldId]); // If reordering the children of a repeating group then make sure that
  // the previous default values get used in setFieldDefaults

  const _defaultValueOptions = !fieldsState.fields[fieldId] && hasReorderedSiblings ? {
    fieldId,
    hasChangedDefault: true
  } : {};

  const fieldsWithoutField = deleteField(fieldsState, fieldId, _defaultValueOptions);

  if (insertType === AS_CHILD) {
    return addChild(fieldsWithoutField, replaceFieldId, field);
  } // if field to replace is top level


  if (fieldsState.fields[replaceFieldId]) {
    let newIndex = fieldsState.fieldsOrder.indexOf(replaceFieldId);

    if (insertType) {
      newIndex = fieldsWithoutField.fieldsOrder.indexOf(replaceFieldId);
      newIndex = insertType === AFTER ? newIndex + 1 : newIndex;
    }

    if (!~newIndex) {
      return fieldsState;
    }

    const newFieldsOrder = fieldsWithoutField.fieldsOrder.asMutable();
    newFieldsOrder.splice(newIndex, 0, fieldId); // If dragging a field from within a repeating group to the top level,
    // then make sure that the group's default value is reset properly

    const parentField = getFieldDirectParent(fieldsState.fields, fieldId);
    const defaultValueOptions = !fieldsState.fields[fieldId] ? {
      fieldId: parentField && parentField.id,
      hasAddedOrDeletedChild: true
    } : {
      fieldId
    };
    return fieldsWithoutField.merge({
      fieldsOrder: newFieldsOrder,
      fields: setFieldDefaults(fieldsWithoutField.fields.set(fieldId, field), defaultValueOptions)
    });
  } // if field to replace is in a field's children, just change fields map


  let path = getFieldPath(fieldsState.fields, replaceFieldId);

  if (insertType) {
    path = getFieldPath(fieldsWithoutField.fields, replaceFieldId);
    const newIndex = path[path.length - 1];
    path[path.length - 1] = insertType === AFTER ? newIndex + 1 : newIndex;
  }

  if (path.length > 1) {
    const pathToChildren = ['fields'].concat(path.slice(0, path.length - 1));
    const newState = fieldsWithoutField.updateIn(pathToChildren, children => {
      const mutableList = children.asMutable();
      mutableList.splice(path[path.length - 1], 0, field);
      return mutableList;
    }); // If you're just re-ordering a field's direct children then make sure
    // that the previous default values get used in setFieldDefaults

    const defaultValueOptions = {
      fieldId,
      hasChangedDefault: !hasReorderedSiblings
    };
    return newState.set('fields', setFieldDefaults(newState.fields, defaultValueOptions));
  }

  return fieldsState;
}
export function createFieldGroup(fieldsState, fieldGroup, children) {
  // remove children from state;
  let newState = fieldsState;
  let index;
  const firstChildId = Object.keys(children)[0];
  const parentField = getFieldDirectParent(fieldsState.fields, firstChildId);

  if (parentField) {
    index = parentField.children.findIndex(_child => children[_child.id]);
  } else {
    index = fieldsState.fieldsOrder.findIndex(_fieldId => children[_fieldId]);
  }

  Object.keys(children).forEach(_childId => {
    newState = deleteField(newState, _childId);
  }); // create new field with type 'group' and add to state

  let newFieldGroup = fieldGroup.merge({
    children: Object.values(children || {}),
    name: getUniqueFieldName(GROUP, fieldsState.fields, fieldGroup.name)
  });
  newFieldGroup = setFieldDefault(newFieldGroup);
  const parentFieldId = parentField ? parentField.id : null;
  return createField(newState, newFieldGroup, parentFieldId, index);
}

function getFieldsByName(fieldsMap) {
  return Object.keys(fieldsMap).reduce((_fieldsByName, _fieldId) => {
    const _field = fieldsMap[_fieldId];
    _fieldsByName[_field.name] = _field;
    return _fieldsByName;
  }, {});
} // Sets global content as default, eventually we want to set
// this in its own property on the module


function editFieldGlobalContent(fieldsState, fieldId, globalContent) {
  const field = getField(fieldsState.fields, fieldId);

  if (field) {
    const hasOccurrences = field.occurrence && Array.isArray(globalContent);
    const isEmptyOccurrenceArray = hasOccurrences && !globalContent.length;

    if (isEmptyOccurrenceArray) {
      globalContent = getDefaultScalarValue(field);
    }

    const fieldChanges = {
      default: globalContent
    };

    if (hasOccurrences) {
      fieldChanges.occurrence = Object.assign({}, field.occurrence, {
        default: isEmptyOccurrenceArray ? 0 : globalContent.length
      });
    }

    const path = getFieldPath(fieldsState.fields, field.id);
    fieldsState = fieldsState.setIn(['fields', ...path], field.merge(fieldChanges));

    if (field.type === GROUP) {
      const fieldGroupScalarVaue = hasOccurrences && !isEmptyOccurrenceArray ? globalContent[0] : globalContent;
      Object.keys(fieldGroupScalarVaue || {}).forEach(_fieldName => {
        const _childField = field.children.find(_child => _child.name === _fieldName);

        if (_childField) {
          fieldsState = editFieldGlobalContent(fieldsState, _childField.id, fieldGroupScalarVaue[_fieldName]);
        }
      });
    }
  }

  return fieldsState;
}

export function editGlobalContent(fieldsState, globalContent) {
  const fieldsByName = getFieldsByName(fieldsState.fields);
  Object.keys(globalContent).forEach(_fieldName => {
    const _field = fieldsByName[_fieldName];

    if (_field) {
      fieldsState = editFieldGlobalContent(fieldsState, _field.id, globalContent[_fieldName]);
    }
  });
  return fieldsState;
}